import { camelCase, upperFirst } from 'lodash';

/**
 * Turns arbitrary labels (spaces, hyphens, etc.) into PascalCase for identifiers.
 * Example: `"medical ride"` → `"MedicalRide"`.
 */
export const toPascalCase = (value: string): string => upperFirst(camelCase(value.trim()));

/**
 * Turns arbitrary labels into camelCase for identifiers.
 * Example: `"medical ride"` → `"medicalRide"`.
 */
export const toCamelCase = (value: string): string => camelCase(value.trim());

export function isImageUrl(url: string): boolean {
  return url.match(/\.(jpeg|jpg|gif|png)$/) !== null;
}

export function extractImageUrlsFromText(text: string): string[] {
  return text.match(/https?:\/\/[^\s]+/g)?.filter(isImageUrl) || [];
}

/**
 *
 * @param str A null, or possibly blank string
 * @returns `true` if the input string is null, undefined, or blank.
 */
export function isStringBlankOrNull(str: string | null): str is null {
  return str === null || str === undefined || isStringBlank(str);
}

/**
 * Checks that the string contains more than just whitespace.
 */
export function isStringBlank(str: string): boolean {
  return str.trim().length === 0;
}

/**
 * Checks that the string contains only lowercase letters (numbers) without whitespaces
 */
export const isLowercaseAlphanumericNoSpaces = (value: string) => {
  return typeof value === 'string' && /^[a-z0-9]+$/.test(value);
};

/**
 * Attempts to convert the string to a number.
 *
 * @returns `null` if the resulting number was NaN.
 */
export function stringToNumberOrNull(str: string): number | null {
  const numericValue = Number(str);
  if (isNaN(numericValue)) return null;
  return numericValue;
}

export const removeAllSpaces = (str: string) => {
  return str.replace(/[ \t]+/g, '');
};

export const isValidJsonString = (str: string) => {
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
};

export const convertStringToArray = (raw: string, symbol: string): string[] => {
  return raw
    .split(symbol)
    .map((line) => line.trim())
    .filter((line) => line.length);
};

export const substitutePlaceholders = (
  message: string,
  data: {
    [key: string]: string | number;
  },
): string => {
  return message.replace(/{(\w+)}/g, (_, key) => data[key]?.toString() || '');
};
